import { formatDate } from '@/components/live';
import { esc, letterheadHtml, type Letterhead, type OtpSignature } from '@/lib/consentDocument';
import { languageName } from '@/lib/languages';
import { en } from '@/lib/consentText/en';
import type { ConsentText } from '@/lib/consentText/types';

const fillIn = (text: string, values: Record<string, string | number | null | undefined>) =>
  text.replace(/\{([a-z]+)\}/g, (whole, key: string) => (values[key] === undefined || values[key] === null ? whole : String(values[key])));

/** The language's name in English: "Kannada" for 'kn'. */
const englishNameOf = (code: string) => languageName(code).replace(/^[A-Z]{2} - /, '');

/**
 * English always, with the farmer's own language beside it (owner, 2026-09-28: "English is must in
 * all aspects ... add a line of consent language: English + other selected language"), in the
 * copy's own language: "English + Kannada" on the English copy, "ಇಂಗ್ಲಿಷ್ + ಕನ್ನಡ" on the Kannada one.
 */
export function consentLanguages(code: string | null | undefined, text: ConsentText = en): string {
  const lang = (code ?? 'en').toLowerCase();
  if (lang === 'en') return text.englishName;
  return text === en ? `English + ${englishNameOf(lang)}` : `${text.englishName} + ${text.selfName}`;
}

/** "English and Kannada" for the declaration, in the copy's language. */
function explainedIn(code: string | null | undefined, text: ConsentText): string {
  const lang = (code ?? 'en').toLowerCase();
  if (lang === 'en') return text.englishName;
  return text === en ? `English and ${englishNameOf(lang)}` : `${text.englishName} ${text.labels.and} ${text.selfName}`;
}

/**
 * The plot consent document (owner, 2026-09-27/28):
 *   1  the farmer, and the programmes the consent covers
 *   2  the plot, its centre point, a sketch of its boundary and the corners' GPS coordinates
 *   3  participation, and the farmer's information and rights - the notice India's data protection
 *      law requires for consent to be valid
 *   4  how the information may be used and shared: the farmer's explicit authorisation, two
 *      statements they tick, and what withdrawing means
 *   5+ one page per standard, with what the farmer takes on and is entitled to
 *   last  the record of consent
 *
 * Every page carries the consent (owner, 2026-09-28: "provide consent info in all pages", "for
 * manual consent block in all pages"): on the paper copy a signature / thumb impression, witness and
 * date block; on every copy a footer naming the kind of consent, its record and the page. The OTP
 * detail is on the final page only. Each page is a
 * `.page` element fixed to A4, so the PDF maker (lib/pdf) and printing give the same pages.
 */
/** Which plot consent: the programme one (standards' pages), or one of the others (a page of its own). */
export type PlotConsentKind = 'programme' | 'sale' | 'family' | 'sharecrop' | 'representative' | 'dataProcessing' | 'trading'
  | 'documentShare' | 'payment' | 'membership' | 'portalAccess' | 'harvestRequest' | 'eudrClaim' | 'withdrawal' | 'amendment';

const KIND_OF: Record<string, PlotConsentKind> = {
  plot_enrolment: 'programme', harvest_sale: 'sale', family_consent: 'family', sharecrop_consent: 'sharecrop',
  representative: 'representative', data_processing: 'dataProcessing', trading: 'trading', document_share: 'documentShare',
  payment: 'payment', membership: 'membership', portal_access: 'portalAccess', harvest_request: 'harvestRequest',
  eudr_fsc_claim: 'eudrClaim', withdrawal: 'withdrawal', consent_amendment: 'amendment',
};

/** The document of a consent row: plot_enrolment -> programme, data_processing -> dataProcessing, ... */
export function plotConsentKind(consentType: string | null | undefined): PlotConsentKind {
  return KIND_OF[consentType ?? ''] ?? 'programme';
}

/** The plot consent types that have this document (programme, sale, family, sharecropping). */
export const PLOT_CONSENT_TYPES = ['plot_enrolment', 'harvest_sale', 'family_consent', 'sharecrop_consent', 'consent_amendment'] as const;
/**
 * Every consent made with this document (owner, 2026-09-28: "convert all of them to bilingual"): the
 * plot consents, and the farmer-level ones - representative, data processing, trading, document
 * share, payment, membership, portal access, harvest request, EUDR / FSC claim, withdrawal.
 */
export const DOCUMENT_CONSENT_TYPES = Object.keys(KIND_OF);
/** Kinds about the farmer rather than one plot: no plot page. */
const PARTY_LEVEL: PlotConsentKind[] = ['representative', 'dataProcessing', 'trading', 'documentShare', 'payment', 'membership',
  'portalAccess', 'harvestRequest', 'eudrClaim', 'withdrawal', 'amendment'];

export interface PlotConsentData {
  kind?: PlotConsentKind;
  /**
   * An amendment (owner, 2026-10-08): the signed consent it changes, and what changes. `changes[].key`
   * is one of the items the text names (mobile, email, address, valid_to, scope_codes, rights_holder,
   * other); `was` and `now` are shown as given.
   */
  amendment?: {
    no: number;
    original: { id: string; title: string; plot: string | null; givenOn: string | null; validFrom: string | null; validTo: string | null };
    askedOn: string | null;
    reason: string | null;
    changes: Array<{ key: string; label?: string | null; was: string | null; now: string }>;
  } | null;
  /** Family and sharecropping: the others who give this consent with the farmer. */
  parties?: Array<{ name: string; relation: string | null; mobile: string | null; share?: number | null }>;
  /** A representative consent: the person appointed, what they may do, and for which plots (null: all). */
  representative?: {
    name: string; relation: string | null; mobile: string | null; fatherOrSpouse?: string | null; photoDataUri?: string | null;
    powers: { acknowledge: boolean; confirmSale: boolean; receivePayment: boolean }; plots: string[] | null;
  } | null;
  /** A withdrawal: its code, the plots withdrawn (null: membership and every plot), why, and from when. */
  withdrawal?: { code: string | null; plots: string[] | null; reason: string | null; effective: string | null } | null;
  /** Sharecropping: the terms on record (share, who is paid); blanks to fill in by hand when absent. */
  terms?: string | null;
  consent: { id: string; language_code: string; valid_from: string | null; valid_to: string | null; scope_codes: string[] };
  organisation: string;
  letterhead?: Letterhead | null;
  farmer: {
    name: string; code: string | null; fatherOrSpouse?: string | null; gender?: string | null; age?: string | null;
    address: string | null; mobile: string | null; email?: string | null; photoDataUri?: string | null;
    kyc?: Array<{ type: string; number: string | null }>;
  };
  plot: {
    code: string; no: string | null; name: string | null; village: string | null; address: string | null;
    declaredArea: string | null; mappedArea: string | null; tenure: string | null; landDocuments?: string | null;
    centroid?: string | null; species?: string | null;
    /**
     * The mapped boundary's outer ring, [longitude, latitude] pairs as GeoJSON has them (owner,
     * 2026-09-28: "need to include GPS coordinates of the plot?"). EUDR asks for the plot's
     * geolocation; the consent shows the centre, a sketch of the shape and the corners.
     */
    boundary?: Array<[number, number]> | null;
    /** The boundary drawn on satellite imagery (mapSnapshot.ts); the sketch is drawn when absent. */
    boundarySnapshot?: string | null;
    /** The excision declaration of a certified plot, in words. */
    excision?: string | null;
  };
  scopes: Array<{ code: string; label: string; certificate?: string | null; validTo?: string | null }>;
}

export interface SignedStamp extends OtpSignature {
  draftFingerprint?: string | null;
}

const STYLE = `
*{box-sizing:border-box}
body{margin:0;background:#e5e7eb;font:13px/1.5 'Noto Sans','Noto Sans Devanagari','Noto Sans Bengali','Noto Sans Kannada','Noto Sans Telugu','Noto Sans Tamil','Noto Sans Gujarati',system-ui,sans-serif;color:#111}
.page{width:794px;height:1123px;margin:0 auto 16px;padding:40px 56px 72px;background:#fff;position:relative;display:flex;flex-direction:column;overflow:hidden}
.page.with-block{padding-bottom:108px}
.ticks{margin:8px 0 4px;border:1px solid #d4d4d4;border-radius:6px;padding:6px 10px;background:#fafafa}.ticks p{display:flex;gap:8px;margin:4px 0}
.tick{font-size:16px;line-height:1;flex:0 0 auto}
.page h1{font-size:19px;margin:0 0 2px}.page h2{font-size:15px;margin:14px 0 6px}.page h3{font-size:13px;margin:10px 0 4px}.sub{color:#555;margin:0 0 12px}
.lh{display:flex;gap:16px;align-items:center;padding-bottom:8px;margin-bottom:12px}.lh-logo{max-height:60px;max-width:150px;object-fit:contain}
.lh-text{font-size:11.5px;color:#444;line-height:1.4}.lh-name{font-size:16px;font-weight:700;margin-bottom:2px}
table{border-collapse:collapse;width:100%}td{border-bottom:1px solid #e2e2e2;padding:5px 4px;vertical-align:top}td:first-child{width:34%;color:#555}
ul{margin:2px 0 6px;padding-left:18px}li{margin:2px 0}
.scope ul{font-size:12.3px;line-height:1.42}.scope p{margin:0 0 4px}
.person{display:flex;gap:18px;align-items:flex-start}.photo{width:110px;height:130px;object-fit:cover;border:1px solid #ccc;border-radius:4px}
.agree{margin-top:auto;border:1px solid #bbb;border-radius:6px;padding:8px 12px;background:#fafafa}
.sign{display:grid;grid-template-columns:1fr 1fr;gap:22px;margin-top:22px}.sign div{border-top:1px solid #333;padding-top:5px;font-size:12px;color:#444}
.thumb{height:76px;border:1px dashed #999;border-radius:6px}
.foot{position:absolute;bottom:14px;left:56px;right:56px;font-size:10px;color:#777;display:flex;justify-content:space-between}
.stage{display:inline-block;font-size:11px;font-weight:700;letter-spacing:.06em;padding:2px 8px;border-radius:4px;margin-bottom:6px}
.stage.draft{color:#9a3412;background:#ffedd5;border:1px solid #fdba74}.stage.signed{color:#14532d;background:#dcfce7;border:1px solid #86efac}
.stage.paper{color:#1e3a8a;background:#dbeafe;border:1px solid #93c5fd}
.block{position:absolute;left:56px;right:56px;bottom:36px;display:grid;grid-template-columns:1.2fr 90px 1fr .6fr;gap:14px;align-items:end;font-size:10.5px;color:#444}
.block span{border-top:1px solid #333;padding-top:3px}.block .tb{height:48px;border:1px dashed #999;border-radius:5px;display:flex;align-items:flex-end;justify-content:center;padding:2px;font-size:9.5px;color:#777}
.wm{position:absolute;top:40%;left:0;right:0;text-align:center;font-size:90px;font-weight:800;color:rgba(154,52,18,.07);transform:rotate(-24deg)}
.given{border:2px solid #15803d;border-radius:8px;padding:12px 14px;margin-top:16px;background:#f0fdf4}.given h2{margin:0 0 6px;color:#14532d}
.rg{display:block;color:#3f3f46;margin-top:1px}
body.bi{font-size:12px;line-height:1.38}body.bi .page{padding:32px 48px 64px}body.bi .page.with-block{padding-bottom:100px}
body.bi td{padding:3px 4px}body.bi .page h2{margin:10px 0 4px}body.bi .page h3{margin:7px 0 3px}body.bi li{margin:1px 0}
body.bi .agree{padding:6px 10px}body.bi .sign{margin-top:14px;gap:14px}body.bi .thumb{height:60px}body.bi .foot{left:48px;right:48px}body.bi .block{left:48px;right:48px}
body.bi .lh{padding-bottom:4px;margin-bottom:8px}li .rg,td .rg{font-size:.96em}.ticks p>span:last-child{flex:1}
.fp{font-family:ui-monospace,Menlo,monospace;font-size:11px;word-break:break-all}
.geo{display:flex;gap:16px;align-items:flex-start;margin-top:4px}.geo svg{flex:0 0 190px;border:1px solid #d4d4d4;border-radius:6px;background:#f8faf8}
.geo-snap{flex:0 0 300px;margin:0}.geo-snap img{display:block;width:300px;height:220px;border:1px solid #d4d4d4;border-radius:6px}
.geo-snap figcaption{font-size:8.5px;color:#666;margin-top:3px}
.corners{flex:1;columns:2;column-gap:18px;font-family:ui-monospace,Menlo,monospace;font-size:10.5px;line-height:1.5;margin:0;padding:0;list-style:none}
.amend th{text-align:left;font-size:11.5px;color:#555;border-bottom:2px solid #d4d4d4;padding:4px}.amend td:nth-child(2){color:#555}.amend td:nth-child(3){color:#14532d;font-weight:600}
.corners li{break-inside:avoid;margin:0}.corners b{display:inline-block;width:22px;font-weight:600;color:#555}
@page{size:A4;margin:0}
@media print{body{background:#fff}.page{width:210mm;height:297mm;margin:0;page-break-after:always;break-after:page;box-shadow:none}.page:last-child{page-break-after:auto;break-after:auto}}`;

/**
 * The plot's geolocation: its centre, a sketch of its outline (north up, corners numbered) and the
 * corners' coordinates to six decimals (about 10 cm). The first 24 corners; the whole boundary is in
 * the app, and in the GeoJSON a due diligence statement carries.
 */
function geolocation(ring: Array<[number, number]> | null | undefined, snapshot: string | null | undefined, t: ConsentText): { centre: string | null; html: string } {
  const points = (ring ?? []).filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
  // A closed ring repeats its first corner at the end; the corners are counted once.
  const corners = points.length > 1 && points[0]![0] === points[points.length - 1]![0] && points[0]![1] === points[points.length - 1]![1]
    ? points.slice(0, -1) : points;
  if (corners.length < 3) return { centre: null, html: '' };
  const lngs = corners.map((point) => point[0]);
  const lats = corners.map((point) => point[1]);
  const centre = `${(lats.reduce((a, b) => a + b, 0) / lats.length).toFixed(6)}, ${(lngs.reduce((a, b) => a + b, 0) / lngs.length).toFixed(6)}`;
  const [minX, maxX, minY, maxY] = [Math.min(...lngs), Math.max(...lngs), Math.min(...lats), Math.max(...lats)];
  // Longitude shrinks towards the poles: scale x by cos(latitude) so the shape is not stretched.
  const k = Math.cos(((minY + maxY) / 2) * Math.PI / 180);
  const spanX = (maxX - minX) * k || 1e-9;
  const spanY = maxY - minY || 1e-9;
  const size = 150;
  const scale = size / Math.max(spanX, spanY);
  const ox = 20 + (size - spanX * scale) / 2;
  const oy = 20 + (size - spanY * scale) / 2;
  const xy = corners.map(([lng, lat]) => [ox + (lng - minX) * k * scale, oy + (maxY - lat) * scale] as const);
  const shown = xy.slice(0, 24);
  const svg = `<svg viewBox="0 0 190 190" width="190" height="190" xmlns="http://www.w3.org/2000/svg">
<polygon points="${xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}" fill="rgba(31,95,58,.12)" stroke="#1f5f3a" stroke-width="1.6"/>
${shown.map(([x, y], index) => `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.4" fill="#1f5f3a"/><text x="${(x + 4).toFixed(1)}" y="${(y - 3).toFixed(1)}" font-size="8" fill="#333">${index + 1}</text>`).join('')}
<text x="176" y="16" font-size="10" text-anchor="middle" fill="#333">N</text><path d="M176 20 L172 30 L176 27 L180 30 Z" fill="#333"/></svg>`;
  const list = corners.slice(0, 24).map(([lng, lat], index) => `<li><b>${index + 1}</b>${lat.toFixed(6)}, ${lng.toFixed(6)}</li>`).join('');
  const more = corners.length > 24 ? `<li>${esc(fillIn(t.labels.moreCorners, { n: corners.length - 24 }))}</li>` : '';
  // The satellite snapshot when there is one; the drawn sketch otherwise.
  const picture = snapshot
    ? `<figure class="geo-snap"><img alt="Plot boundary on satellite imagery" src="${esc(snapshot)}"/><figcaption>${esc(t.labels.snapshotCaption)} · Imagery © Esri, Maxar, Earthstar Geographics</figcaption></figure>`
    : svg;
  return { centre, html: `<div class="geo">${picture}<ul class="corners">${list}${more}</ul></div>` };
}


/** The first and last two characters, the rest masked (owner, 2026-09-28): the full values are in the app. */
export function maskMiddle(value: string): string {
  const plain = value.replace(/-/g, '');
  return plain.length <= 4 ? plain : `${plain.slice(0, 2)}${'*'.repeat(8)}${plain.slice(-2)}`;
}

/**
 * Page 1's contact details, masked (owner, 2026-09-28: "first page... mask the phone number, address"):
 * the document travels by WhatsApp and email and may be printed, so it carries only enough for the
 * farmer to recognise their own. The full details stay in the app.
 *   mobile   +91 ******0001
 *   address  every part but the last two (district, state) cut to its first two letters; PIN 58****
 *   email    ra****@example.com
 */
export function maskPhone(value: string | null | undefined): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 6) return '****';
  const country = value.trim().startsWith('+') && digits.length > 10 ? `+${digits.slice(0, digits.length - 10)} ` : '';
  return `${country}${'*'.repeat(6)}${digits.slice(-4)}`;
}
export function maskAddress(value: string | null | undefined): string | null {
  if (!value) return null;
  const pin = value.match(/\b(\d{6})\b/);
  const body = value.replace(/\s*[-,]?\s*\b\d{6}\b/, '').trim();
  const parts = body.split(',').map((part) => part.trim()).filter(Boolean);
  const keep = parts.length > 2 ? 2 : Math.max(parts.length - 1, 0);
  const masked = parts.map((part, index) => (index >= parts.length - keep ? part : `${part.slice(0, 2)}****`));
  return `${masked.join(', ')}${pin?.[1] ? ` - ${pin[1].slice(0, 2)}****` : ''}`;
}
export function maskEmail(value: string | null | undefined): string | null {
  if (!value || !value.includes('@')) return value ?? null;
  const [local = '', domain = ''] = value.split('@');
  return `${local.slice(0, 2)}****@${domain}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "28-Sep-2026" and "10:05 AM", India time. Fixed three-letter months: some date libraries write "Sept". */
function dayAndTime(iso: string): { day: string; time: string } {
  const at = new Date(iso);
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' })
    .formatToParts(at).map((part) => [part.type, part.value]));
  return {
    day: `${parts.day}-${MONTHS[Number(parts.month) - 1]}-${parts.year}`,
    time: new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).format(at),
  };
}

function reachedOn(stamp: SignedStamp, t: ConsentText): string {
  const last4 = (masked: string) => `****${masked.replace(/\D/g, '').slice(-4) || masked.slice(-4)}`;
  const phones = [...new Set(stamp.channels.filter((entry) => entry.channel !== 'email').map((entry) => last4(entry.destination)))];
  const emails = [...new Set(stamp.channels.filter((entry) => entry.channel === 'email').map((entry) => entry.destination))];
  const via = [...new Set(stamp.channels.filter((entry) => entry.channel !== 'email').map((entry) => (entry.channel === 'sms' ? 'SMS' : 'WhatsApp')))];
  return [phones.length ? `${t.labels.registeredMobile} ${phones.join(', ')}${via.length ? ` ${t.labels.via} ${via.join('/')}` : ''}` : null,
    emails.length ? `${t.labels.registeredEmail} ${emails.join(', ')}` : null].filter(Boolean).join(` ${t.labels.and} `) || t.labels.registeredContact;
}

/**
 * The consent stamp in the owner's words (2026-09-28), with the farmer's own name: "Consent of
 * Ramappa Gowda was obtained through OTP sent to registered mobile ****0001 via SMS/WhatsApp and
 * verified on 28-Sep-2026 at 10:05 AM by Suresh Naik (Field Officer)." In the copy's language.
 */
export function otpSentence(stamp: SignedStamp, farmerName?: string | null, t: ConsentText = en): string {
  const { day, time } = dayAndTime(stamp.verifiedAt);
  return fillIn(t.labels.otpSentence, {
    name: farmerName || t.labels.farmer, reached: reachedOn(stamp, t), day, time,
    by: stamp.verifiedBy ? fillIn(t.labels.otpBy, { officer: stamp.verifiedBy }) : '',
  });
}

/**
 * One document, both languages (owner, 2026-09-28: "all docs generated as bilingual rather than
 * english and regional separate", "stacked layout for all four"): every heading, paragraph and list
 * item in English with the farmer's own language under it; short labels on one line, "Name / ಹೆಸರು".
 * A farmer whose language is English gets English alone.
 *
 * 'draft'  - to read before consenting, watermarked DRAFT
 * 'paper'  - the print copy for a farmer who signs by hand or thumb: no watermark, a signature,
 *            thumb impression, witness and date block on every page, the full block at the end;
 *            signed, it is uploaded as the consent's signed copy
 * 'signed' - consent given through OTP: the full record, with the OTP detail, on the final page
 *
 * Pages are logical: one that runs longer than A4 is continued on the next sheet by the PDF maker
 * (lib/pdf), which then numbers the sheets.
 */
export function buildPlotConsentHtml(data: PlotConsentData, stage: 'draft' | 'paper' | 'signed', stamp?: SignedStamp | null,
                                     local: ConsentText = en, generatedAt: string = new Date().toISOString()): string {
  const texts: ConsentText[] = local === en ? [en] : [en, local];
  const kind = data.kind ?? 'programme';
  const kindText = (t: ConsentText) => (kind === 'programme' ? null : t.kinds[kind]);
  const partyLevel = PARTY_LEVEL.includes(kind);
  const wd = kind === 'withdrawal' ? data.withdrawal ?? null : null;
  const isKind = kind !== 'programme';
  const signed = stage === 'signed' && stamp;
  const name = data.farmer.name;
  const rep = kind === 'representative' ? data.representative ?? null : null;
  const plotRef = kind === 'representative' ? (rep?.plots?.length ? rep.plots.join(', ') : en.kinds.representative.plotsAll)
    : kind === 'withdrawal' ? (wd?.plots?.length ? wd.plots.join(', ') : en.kinds.withdrawal.allPlots)
    : partyLevel ? '' : [data.plot.code, data.plot.no].filter(Boolean).join(' · ');
  const code = data.consent.language_code;
  // Placeholders in every text: the organisation by its own name (owner, 2026-09-28).
  const fill = (text: string, extra: Record<string, string | number | null | undefined> = {}) =>
    fillIn(text, { org: data.organisation, name, plot: plotRef, n: data.amendment?.no ?? '', ...extra });

  /** A paragraph or heading: English, the farmer's language under it. */
  const bi = (pick: (t: ConsentText) => string) => texts.map((t, index) =>
    (index === 0 ? esc(pick(t)) : `<span class="rg">${esc(pick(t))}</span>`)).join('');
  /** A short label: "Name / ಹೆಸರು". */
  const lab = (pick: (t: ConsentText) => string) => [...new Set(texts.map((t) => pick(t)))].map(esc).join(' / ');
  /** A list: each item in English, the same item in the farmer's language under it. */
  const list = (pick: (t: ConsentText) => string[]) => `<ul>${pick(en).map((_, index) =>
    `<li>${bi((t) => fill(pick(t)[index] ?? ''))}</li>`).join('')}</ul>`;
  const row = (label: string, value: string | null | undefined, raw = false) =>
    `<tr><td>${label}</td><td>${raw ? (value || '--') : esc(value || '--')}</td></tr>`;

  const mark = stage === 'draft' ? `<div class="wm">${esc(en.labels.watermark)}</div>` : '';
  const badge = `<span class="stage ${stage}">${lab((t) => (stage === 'draft' ? t.labels.stageDraft : stage === 'paper' ? t.labels.stagePaper : t.labels.stageSigned))}</span>`;
  const open = (last = false) => `<section class="page${!last && stage === 'paper' ? ' with-block' : ''}">`;
  const perPage = stage === 'paper'
    ? `<div class="block"><span>${lab((t) => fill(t.labels.signatureOf))}</span><div class="tb">${lab((t) => t.labels.thumb)}</div><span>${lab((t) => t.labels.witness)}</span><span>${lab((t) => t.labels.date)}</span></div>`
    : '';
  const kindWord = signed ? en.labels.kindDigital : stage === 'paper' ? en.labels.kindPaper : en.labels.kindDraft;
  const box = signed ? '&#9745;' : '&#9744;';
  const generated = dayAndTime(generatedAt);
  const generatedLine = fillIn(en.labels.generated, { at: `${generated.day}, ${generated.time}` });
  // The footer names the consent, its record and the sheet; the sheet numbers are written by the PDF
  // maker once long pages are continued. Not on the last page: it carries the full signature block.
  const close = (last = false) => `${last ? '' : perPage}<div class="foot"><span>${esc(data.organisation)} · ${esc(partyLevel ? (data.farmer.code ?? name) : `${en.labels.plot} ${data.plot.code}`)} · ${esc(generatedLine)}</span><span>${esc(kindWord)} | ${esc(en.labels.record)} ${esc(data.consent.id.slice(0, 8))} | ${esc(en.labels.page)} <span class="pno"></span></span></div></section>`;
  const head = letterheadHtml(data.letterhead, data.organisation);
  const scopeNames = data.scopes.map((scope) => scope.label).join(', ');
  const valid = (t: ConsentText) => fillIn(t.labels.validRange, { from: data.consent.valid_from ? formatDate(data.consent.valid_from) : '--', to: data.consent.valid_to ? formatDate(data.consent.valid_to) : '--' });
  const contact = [data.letterhead?.office_line, data.letterhead?.contact_line].filter(Boolean).join(' · ');
  const gender = data.farmer.gender ? lab((t) => t.genders[data.farmer.gender as keyof ConsentText['genders']] ?? data.farmer.gender!) : '--';
  const languagesLine = lab((t) => consentLanguages(code, t));
  let n = 0;
  const h2 = (pick: (t: ConsentText) => string) => { n += 1; return `<h2>${n}. ${bi((t) => pick(t).replace(/^\s*\d+\.\s*/, ''))}</h2>`; };

  const farmerPage = `${open()}${mark}${head}${badge}
<h1>${bi((t) => kindText(t)?.title ?? t.labels.title)}</h1>
<p class="sub">${plotRef ? `${esc(plotRef)} · ` : ''}${lab((t) => (kindText(t) ? fill(kindText(t)!.consentValue) : (scopeNames || t.labels.membershipOfGroup)))} · ${lab((t) => `${t.labels.valid} ${valid(t)}`)}</p>
${h2((t) => t.labels.farmerHeading)}
<div class="person">${data.farmer.photoDataUri ? `<img class="photo" src="${data.farmer.photoDataUri}" alt="">` : ''}
<table>${row(lab((t) => t.labels.name), name)}${row(lab((t) => t.labels.memberCode), data.farmer.code)}${row(lab((t) => t.labels.fatherOrSpouse), data.farmer.fatherOrSpouse)}
${row(lab((t) => t.labels.gender), gender, true)}${row(lab((t) => t.labels.age), data.farmer.age)}${row(lab((t) => t.labels.address), maskAddress(data.farmer.address))}
${row(lab((t) => t.labels.mobile), maskPhone(data.farmer.mobile))}${row(lab((t) => t.labels.email), maskEmail(data.farmer.email))}
${(data.farmer.kyc ?? []).map((item) => row(esc(item.type), item.number)).join('')}${row(lab((t) => t.labels.consentLanguage), languagesLine, true)}</table></div>
${isKind ? `<h3>${lab((t) => t.labels.coversHeading)}</h3><p>${bi((t) => fill(kindText(t)!.about))}</p>`
  : `<h3>${lab((t) => t.labels.programmesHeading)}</h3>
<table>${data.scopes.length ? data.scopes.map((scope) => row(esc(scope.label), [scope.certificate ? `${lab((t) => t.labels.certificateReference)} ${esc(scope.certificate)}` : null,
  scope.validTo ? lab((t) => fillIn(t.labels.validUntil, { date: formatDate(scope.validTo!) })) : null].filter(Boolean).join(' · ') || lab((t) => fill(t.labels.inScope)), true)).join('')
  : row(lab((t) => t.labels.membership), lab((t) => fill(t.labels.membershipValue)), true)}</table>`}
${partyLevel ? '' : `<div class="agree">${bi((t) => fill(t.labels.agreeFarmer, { languages: explainedIn(code, t) }))}</div>`}
${close()}`;

  const geo = geolocation(data.plot.boundary, data.plot.boundarySnapshot, en);
  const plotPage = () => `${open()}${mark}${head}
${h2((t) => t.labels.plotHeading)}
<table>${row(lab((t) => t.labels.plot), [data.plot.code, data.plot.no, data.plot.name].filter(Boolean).join(' · '))}
${row(lab((t) => t.labels.village), data.plot.village)}${row(lab((t) => t.labels.location), data.plot.address)}
${row(lab((t) => t.labels.centre), geo.centre ?? data.plot.centroid ?? null) }
${row(lab((t) => t.labels.declaredArea), data.plot.declaredArea)}${row(lab((t) => t.labels.mappedArea), data.plot.mappedArea)}${row(lab((t) => t.labels.tenure), data.plot.tenure)}
${row(lab((t) => t.labels.landDocuments), data.plot.landDocuments)}${row(lab((t) => t.labels.trees), data.plot.species)}
${data.plot.excision ? row(lab((t) => t.labels.excision), data.plot.excision) : ''}</table>
${geo.html ? `<h3>${lab((t) => t.labels.boundaryHeading)}</h3>${geo.html}` : ''}
<div class="agree">${isKind ? bi((t) => `${fill(kindText(t)!.consentValue)}: ${plotRef}`) : bi((t) => fill(t.labels.agreePlot))}</div>
${close()}`;

  // Sale, family, sharecropping: the consent's own page. The family's and sharecropping consents name
  // the others who give it, each with a signature line (they sign the print copy).
  const parties = data.parties ?? [];
  const partyTable = parties.length
    ? `<table><tr><td>${lab((t) => t.labels.name)}</td><td>${lab((t) => t.labels.relationship)} · ${lab((t) => t.labels.mobile)}</td></tr>${parties.map((party) =>
        row(esc(party.name), [party.relation, maskPhone(party.mobile), party.share != null ? `${party.share} %` : null].filter(Boolean).join(' · '))).join('')}</table>`
    : `<p class="sub">${bi((t) => t.labels.partiesBlank)}</p>`;
  const partySigns = (parties.length ? parties.map((party) => party.name) : ['________________', '________________']).map((person) =>
    `<div>${lab((t) => fillIn(t.labels.partySignature, { name: person }))}</div><div class="thumb"></div>`).join('');
  const kindPage = () => isKind ? `${open()}${mark}${head}<div class="scope">
${h2((t) => kindText(t)!.heading)}
${kind === 'family' || kind === 'sharecrop' ? `<h3>${lab((t) => t.labels.partiesHeading)}</h3>${partyTable}` : ''}
${wd ? `<table>${row(lab((t) => t.kinds.withdrawal.plots), lab((t) => (wd.plots?.length ? wd.plots.join(', ') : t.kinds.withdrawal.allPlots)), true)}
${row(lab((t) => t.kinds.withdrawal.reason), wd.reason)}${row(lab((t) => t.kinds.withdrawal.effective), wd.effective ? formatDate(wd.effective) : null)}
${wd.code ? row(lab((t) => t.labels.recordId), wd.code) : ''}</table>` : ''}
${kind === 'sharecrop' ? `<h3>${lab((t) => t.labels.termsHeading)}</h3><p>${data.terms ? esc(data.terms) : lab((t) => t.labels.termsBlank)}</p>` : ''}
<h3>${bi((t) => fill(kindText(t)!.agreeLead))}</h3>${list((t) => kindText(t)!.obligations)}
<h3>${bi((t) => fill(kindText(t)!.rightsLead))}</h3>${list((t) => kindText(t)!.rights)}</div>
${kind === 'family' || kind === 'sharecrop' ? `<div class="sign">${partySigns}</div><p class="sub">${bi((t) => t.labels.partiesNote)}</p>` : ''}
<div class="agree">${bi((t) => fill(kindText(t)!.agree, { languages: explainedIn(code, t) }))}</div>
${close()}` : '';

  // The representative: who they are, the matters ticked, the plots, and both signatures.
  const tick = (on: boolean) => (on ? '&#9745;' : '&#9744;');
  const repPage = () => rep ? `${open()}${mark}${head}<div class="scope">
${h2((t) => t.kinds.representative.heading)}
<div class="person">${rep.photoDataUri ? `<img class="photo" src="${rep.photoDataUri}" alt="">` : ''}
<table>${row(lab((t) => t.labels.name), rep.name)}${row(lab((t) => t.labels.relationship), rep.relation)}
${row(lab((t) => t.labels.fatherOrSpouse), rep.fatherOrSpouse ?? null)}${row(lab((t) => t.labels.mobile), maskPhone(rep.mobile))}
${row(lab((t) => t.labels.plot), lab((t) => (rep.plots?.length ? fillIn(t.kinds.representative.plotsSome, { plots: rep.plots.join(', ') }) : t.kinds.representative.plotsAll)), true)}</table></div>
<h3>${bi((t) => t.kinds.representative.powersHeading)}</h3>
<div class="ticks">${(['acknowledge', 'confirmSale', 'receivePayment'] as const).map((power) =>
  `<p><span class="tick">${tick(rep.powers[power])}</span><span>${bi((t) => t.kinds.representative.powers[power])}</span></p>`).join('')}</div>
<h3>${bi((t) => fill(t.kinds.representative.agreeLead))}</h3>${list((t) => t.kinds.representative.obligations)}
<h3>${bi((t) => fill(t.kinds.representative.rightsLead))}</h3>${list((t) => t.kinds.representative.rights)}</div>
<div class="sign"><div>${lab((t) => fill(t.labels.signatureOf))}</div><div class="thumb"></div><div>${lab((t) => fillIn(t.labels.partySignature, { name: rep.name }))}</div><div class="thumb"></div></div>
<div class="agree">${bi((t) => fill(t.kinds.representative.agree, { languages: explainedIn(code, t) }))}</div>
${close()}` : '';

  const dataPage = () => `${open()}${mark}${head}
${h2((t) => t.labels.dataHeading)}
<h3>${bi((t) => t.labels.dutiesHeading)}</h3>
${list((t) => t.membershipDuties)}
<h3>${bi((t) => t.labels.informationHeading)}</h3>
<table>${row(lab((t) => t.labels.collected), bi((t) => fill(t.dataNotice.collected)), true)}${row(lab((t) => t.labels.why), bi((t) => fill(t.dataNotice.purposes)), true)}
${row(lab((t) => t.labels.whoReceives), bi((t) => fill(t.dataNotice.sharedWith)), true)}${row(lab((t) => t.labels.howLong), bi((t) => fill(t.dataNotice.retention)), true)}
${row(lab((t) => t.labels.myRights), bi((t) => fill(t.dataNotice.rights)), true)}
${row(lab((t) => t.labels.toAsk), bi((t) => `${data.organisation}${contact ? ` - ${contact}` : ''}. ${t.labels.dataBoard}`), true)}</table>
<div class="agree">${bi((t) => fill(t.labels.agreeData))}</div>
${close()}`;

  const sharingPage = () => `${open()}${mark}${head}
${h2((t) => t.labels.sharingHeading)}
<p>${bi((t) => fill(t.sharing.intro))}</p>
${list((t) => t.sharing.recipients)}
${en.sharing.limits.map((_, index) => `<p>${bi((t) => fill(t.sharing.limits[index] ?? ''))}</p>`).join('')}
<div class="ticks">${en.sharing.statements.map((_, index) => `<p><span class="tick">${box}</span><span>${bi((t) => fill(t.sharing.statements[index] ?? ''))}</span></p>`).join('')}</div>
<h3>${bi((t) => t.labels.withdrawingHeading)}</h3>
<p>${bi((t) => fill(t.sharing.withdrawal))}</p>
${close()}`;

  const scopePages = () => data.scopes.map((scope) => {
    const text = (t: ConsentText) => t.scopes[scope.code as keyof ConsentText['scopes']];
    return `${open()}${mark}${head}<div class="scope">
${h2((t) => text(t)?.title ?? scope.label)}
<p>${bi((t) => fill(text(t)?.about ?? ''))}${scope.certificate ? ` <span class="sub">${lab((t) => t.labels.certificateReference)}: ${esc(scope.certificate)}</span>` : ''}</p>
<h3>${lab((t) => t.labels.iAgreeTo)}</h3>${list((t) => text(t)?.obligations ?? [])}
<h3>${lab((t) => t.labels.iAmEntitled)}</h3>${list((t) => [...(text(t)?.rights ?? []), ...t.commonRights])}</div>
<div class="agree">${bi((t) => fill(t.labels.agreeScope, { scope: scope.label }))}</div>
${close()}`;
  }).join('');

  // An amendment (owner, 2026-10-08): the consent it amends, the table of what changes against what
  // was, what stays the same, and the farmer's rights around it.
  const amendmentPage = () => {
    const a = data.amendment;
    if (kind !== 'amendment' || !a) return '';
    const at = (t: ConsentText) => t.kinds.amendment;
    const items = (t: ConsentText) => at(t).changeItems as Record<string, string>;
    return `${open()}${mark}${head}<div class="scope">
${h2((t) => at(t).originalHeading)}
<table>${row(lab((t) => at(t).consentAmended), `${esc(a.original.title)}${a.original.plot ? ` · ${esc(a.original.plot)}` : ''}`, true)}
${row(lab((t) => at(t).givenOn), a.original.givenOn ? formatDate(a.original.givenOn) : null)}
${row(lab((t) => t.labels.valid.replace(/^\p{Ll}/u, (c) => c.toUpperCase())), lab((t) => fillIn(t.labels.validRange, { from: a.original.validFrom ? formatDate(a.original.validFrom) : '--', to: a.original.validTo ? formatDate(a.original.validTo) : '--' })), true)}
${row(lab((t) => at(t).askedFor), a.askedOn ? formatDate(a.askedOn) : null)}${a.reason ? row(lab((t) => at(t).reason), a.reason) : ''}
${row(lab((t) => t.labels.recordId), a.original.id)}</table>
${h2((t) => at(t).heading)}
<p>${bi((t) => fill(at(t).about))}</p>
<table class="amend"><tr><th>${lab((t) => at(t).item)}</th><th>${lab((t) => at(t).was)}</th><th>${lab((t) => at(t).now)}</th></tr>
${a.changes.map((change) => {
  const shown = (value: string | null) => (value && change.key === 'valid_to' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : value);
  return `<tr><td>${lab((t) => items(t)[change.key] ?? change.label ?? change.key)}</td><td>${esc(shown(change.was) || '--')}</td><td>${esc(shown(change.now) ?? '')}</td></tr>`;
}).join('')}</table>
<h3>${bi((t) => at(t).agreeLead)}</h3>${list((t) => at(t).obligations)}
<h3>${lab((t) => at(t).rightsLead)}</h3>${list((t) => at(t).rights)}</div>
<div class="agree">${bi((t) => fill(at(t).agree, { languages: explainedIn(code, t) }))}</div>
${close()}`;
  };

  // Built in reading order, so the headings number themselves.
  // Farmer-level consents: the farmer, the consent's own page, and for data processing the full notice
  // and how information is shared (the other consents rest on it).
  const body = kind === 'representative'
    ? `${farmerPage}${repPage()}${dataPage()}`
    : kind === 'amendment' ? `${farmerPage}${amendmentPage()}`
    : kind === 'dataProcessing' ? `${farmerPage}${kindPage()}${dataPage()}${sharingPage()}`
    : partyLevel ? `${farmerPage}${kindPage()}`
    : isKind
    ? `${farmerPage}${plotPage()}${kindPage()}${kind === 'sale' ? sharingPage() : dataPage()}`
    : `${farmerPage}${plotPage()}${dataPage()}${sharingPage()}${scopePages()}`;

  const recordPage = `${open(true)}${mark}${head}
${h2((t) => t.labels.recordHeading)}
<table>${row(lab((t) => t.labels.consent), lab((t) => (kindText(t) ? fill(kindText(t)!.consentValue) : fill(t.labels.consentValue, { scopes: scopeNames || t.labels.membershipOfGroup }))), true)}
${row(lab((t) => t.labels.farmer), `${name} (${data.farmer.code ?? '--'})`)}
${partyLevel ? '' : row(lab((t) => t.labels.plot), data.plot.code)}${row(lab((t) => t.labels.valid), lab(valid), true)}${row(lab((t) => t.labels.consentLanguage), languagesLine, true)}
${row(lab((t) => t.labels.recordId), data.consent.id)}</table>
${texts.length > 1 ? `<p class="sub">${bi((t) => t.labels.bothVersions)}</p>` : ''}
${signed ? `<div class="given"><h2>${lab((t) => t.labels.givenByOtp)}</h2>
<div>${bi((t) => otpSentence(stamp, name, t))}</div>
<div style="margin-top:6px">${lab((t) => t.labels.otpRef)}: <span class="fp">${esc(maskMiddle(stamp.reference))}</span></div>
${stamp.draftFingerprint ? `<div>${lab((t) => t.labels.documentSha)}: <span class="fp">${esc(maskMiddle(stamp.draftFingerprint))}</span></div>` : ''}
</div>` : `<div class="sign"><div>${lab((t) => fill(t.labels.signatureOf))}</div><div class="thumb"></div>
<div>${lab((t) => t.labels.witnessFull)}</div><div>${lab((t) => t.labels.officerFull)}</div></div>
<p class="sub">${bi((t) => t.labels.paperNote)}</p>`}
${close(true)}`;

  return `<!doctype html><html lang="${esc(texts.length > 1 ? code : 'en')}"><head><meta charset="utf-8">
<title>${esc(kindText(en) ? fill(kindText(en)!.consentValue) : 'Plot consent')} ${esc(partyLevel ? (data.farmer.code ?? '') : data.plot.code)} - ${esc(name)}</title><style>${STYLE}</style></head>
<body${texts.length > 1 ? ' class="bi"' : ''}>${body}${recordPage}</body></html>`;
}

/** The SHA-256 of a PDF or any bytes, as hex: printed (masked) on the signed copy against the draft. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
